import { useEffect } from 'react'

/**
 * Keep `--app-height` equal to the height a person can actually see.
 *
 * `100vh` is the wrong number on a phone and always has been: it is the height
 * the page *would* have with the browser's toolbars hidden, so a shell sized to
 * it hangs its last row — here, the composer — behind the address bar with no
 * way to scroll down to it. `100dvh` fixes that much, and `tokens.css` uses it
 * wherever it exists.
 *
 * What `dvh` does not cover is the keyboard. On iOS an on-screen keyboard does
 * not resize the layout viewport at all; it only shrinks the *visual* one and
 * scrolls the page under it, which puts the composer somewhere below the keys
 * that are typing into it. `visualViewport` is the only thing that reports
 * that, so this narrows the shell to what is left above the keyboard and the
 * flex layout does the rest — the stream gives up the height and the composer
 * stays on screen.
 *
 * Chrome on Android is told the same thing declaratively, by
 * `interactive-widget=resizes-content` in the viewport meta; there
 * `visualViewport.height` already matches and this writes the value it
 * would have had anyway.
 *
 * Two details are deliberate:
 *
 *   - `offsetTop` is added back. iOS scrolls the layout viewport to reveal the
 *     focused field, so the visual viewport starts partway down; without it the
 *     shell is short by however far the page was pushed and a gap opens under
 *     the composer.
 *   - Nothing is written when there is no `visualViewport` — a browser without
 *     it keeps the `dvh` (or `vh`) value from the stylesheet, which is the
 *     right answer rather than a broken one.
 */
export function useAppHeight(): void {
  useEffect(() => {
    const viewport = typeof window === 'undefined' ? undefined : window.visualViewport
    if (!viewport) return

    const root = document.documentElement
    let frame = 0

    const apply = () => {
      cancelAnimationFrame(frame)
      // Coalesced into a frame: iOS fires `resize` and `scroll` together for a
      // single keyboard opening, and each one would otherwise write a style and
      // force a layout.
      frame = requestAnimationFrame(() => {
        root.style.setProperty(
          '--app-height', `${Math.round(viewport.height + viewport.offsetTop)}px`)
      })
    }

    apply()
    viewport.addEventListener('resize', apply)
    viewport.addEventListener('scroll', apply)

    return () => {
      cancelAnimationFrame(frame)
      viewport.removeEventListener('resize', apply)
      viewport.removeEventListener('scroll', apply)
      // Back to the stylesheet's value. Leaving a stale pixel height behind
      // would outlive the component that measured it.
      root.style.removeProperty('--app-height')
    }
  }, [])
}
